// 사장님 CRM: 고객 앱 예약 요청 큐
// PendingBookings 목록 + 매칭+승인 다이얼로그
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';
import './PendingBookings.css';

function PendingBookings() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [suggestions, setSuggestions] = useState(null);
    const [error, setError] = useState(null);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/pending-bookings');
            setItems(res.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.error || '목록 조회 실패');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const openDialog = async (id) => {
        setSelectedId(id);
        setSuggestions(null);
        try {
            const res = await api.get(`/pending-bookings/${id}/match-suggestions`);
            setSuggestions(res.data?.data);
        } catch (err) {
            setError(err.response?.data?.error || '매칭 후보 조회 실패');
        }
    };

    const closeDialog = () => { setSelectedId(null); setSuggestions(null); };

    const approve = async (matchType, payload) => {
        try {
            await api.post(`/pending-bookings/${selectedId}/approve`, { matchType, ...payload });
            closeDialog();
            fetchItems();
        } catch (err) {
            setError(err.response?.data?.error || err.message || '승인 실패');
        }
    };

    const reject = async () => {
        const reason = window.prompt('거절 사유를 입력하세요 (선택):') || '';
        try {
            await api.post(`/pending-bookings/${selectedId}/reject`, { reason });
            closeDialog();
            fetchItems();
        } catch (err) {
            setError(err.response?.data?.error || '거절 실패');
        }
    };

    if (loading) return <div className="pb-loading">불러오는 중...</div>;

    return (
        <div className="pending-bookings-page">
            <h2>예약 요청 큐 <span className="pb-badge">{items.length}</span></h2>
            {error && <div className="pb-error">{error}</div>}
            {items.length === 0 ? (
                <p className="pb-empty">처리 대기 중인 예약 요청이 없습니다.</p>
            ) : (
                <ul className="pb-list">
                    {items.map(it => (
                        <li key={it.id} className="pb-card" onClick={() => openDialog(it.id)}>
                            <div className="pb-time">{formatDateTime(it.requested_start_time)}</div>
                            <div className="pb-info">
                                <div><strong>{it.display_name || it.account_display_name || '이름 없음'}</strong>
                                    {it.phone_hint_masked && <span className="pb-hint"> · {it.phone_hint_masked}</span>}
                                </div>
                                <div className="pb-vehicle">{it.plateNumber}</div>
                                {it.memo && <div className="pb-memo">{it.memo}</div>}
                            </div>
                            <div className="pb-action">처리하기 →</div>
                        </li>
                    ))}
                </ul>
            )}

            {selectedId && suggestions && (
                <ApprovalDialog
                    suggestions={suggestions}
                    onApprove={approve}
                    onReject={reject}
                    onCancel={closeDialog}
                />
            )}
        </div>
    );
}

function ApprovalDialog({ suggestions, onApprove, onReject, onCancel }) {
    const {
        pendingBooking: pb,
        suggestions: s,
        menu: menuInfo,
        options: optionsInfo,
        carModel: carModelInfo,
        sizes: shopSizes,
        sizeContext,
    } = suggestions;
    const candidates = dedup([
        ...(s.phoneMatches || []),
        ...(s.plateMatches || []).filter(p => p.customer_id).map(p => ({
            id: p.customer_id, customerName: p.customerName, points: p.points, last_visit: p.last_visit,
            via: 'plate'
        })),
        ...(s.nameMatches || []),
    ], 'id');

    const [mode, setMode] = useState(candidates.length > 0 ? 'existing' : 'new');
    const [selectedCustomerId, setSelectedCustomerId] = useState(candidates[0]?.id || null);
    const [newName, setNewName] = useState(pb.display_name || '');
    // phone_hint는 마스킹된 값만 보임. 사장님이 신규 등록 탭에서 직접 입력
    const [newPhone, setNewPhone] = useState('');
    const [confirmedSizeId, setConfirmedSizeId] = useState(sizeContext?.suggestedSizeId || '');

    const submit = () => {
        const sizePayload = confirmedSizeId ? { confirmedSizeId: parseInt(confirmedSizeId, 10) } : {};
        if (mode === 'existing') {
            if (!selectedCustomerId) return alert('고객을 선택해주세요.');
            onApprove('existing', { customerId: selectedCustomerId, ...sizePayload });
        } else {
            if (!newName.trim()) return alert('고객명을 입력해주세요.');
            onApprove('new', { customerName: newName.trim(), phoneNumber: newPhone.trim() || undefined, ...sizePayload });
        }
    };

    return (
        <div className="pb-modal-backdrop" onClick={onCancel}>
            <div className="pb-modal" onClick={e => e.stopPropagation()}>
                <h3>예약 승인 — 고객 매칭</h3>
                <div className="pb-summary">
                    <div><strong>시간:</strong> {formatDateTime(pb.requested_start_time)}</div>
                    <div><strong>차량:</strong> {pb.plateNumber}
                        {carModelInfo && (
                            <span className="pb-meta">
                                {' '}({carModelInfo.manufacturer_name} {carModelInfo.modelName}
                                {carModelInfo.vehicleClass && ` · ${carModelInfo.vehicleClass}`})
                            </span>
                        )}
                    </div>
                    {menuInfo && (
                        <div><strong>메뉴:</strong> {menuInfo.menuName}
                            {menuInfo.duration_minutes && ` (${menuInfo.duration_minutes}분)`}
                        </div>
                    )}
                    {optionsInfo && optionsInfo.length > 0 && (
                        <div><strong>옵션:</strong> {optionsInfo.map(o => o.optionName).join(', ')}</div>
                    )}
                    {pb.memo && <div><strong>메모:</strong> {pb.memo}</div>}
                </div>

                <div className="pb-size-section">
                    <label className="pb-size-label">차량 사이즈 (이 매장 기준)</label>
                    <select
                        className="pb-size-select"
                        value={confirmedSizeId || ''}
                        onChange={e => setConfirmedSizeId(e.target.value)}
                    >
                        <option value="">- 선택 -</option>
                        {(shopSizes || []).map(sz => (
                            <option key={sz.id} value={sz.id}>{sz.sizeName}</option>
                        ))}
                    </select>
                    {sizeContext && (
                        <div className="pb-size-hint">
                            {sizeContext.existingVehicleSizeId && '✓ 이전 방문 사이즈'}
                            {!sizeContext.existingVehicleSizeId && sizeContext.autoMappedSizeId && '✓ 차종 매핑 자동'}
                            {!sizeContext.existingVehicleSizeId && !sizeContext.autoMappedSizeId && sizeContext.customerSelectedSizeId && '⚠ 고객 임의 선택'}
                            {!sizeContext.existingVehicleSizeId && !sizeContext.autoMappedSizeId && !sizeContext.customerSelectedSizeId && '— 매장 기본 사이즈'}
                        </div>
                    )}
                </div>

                <div className="pb-tabs">
                    <button className={mode === 'existing' ? 'active' : ''} onClick={() => setMode('existing')}>
                        기존 고객 매칭
                    </button>
                    <button className={mode === 'new' ? 'active' : ''} onClick={() => setMode('new')}>
                        신규 등록
                    </button>
                </div>

                {mode === 'existing' && (
                    <div className="pb-candidates">
                        {candidates.length === 0 ? (
                            <p>매칭 후보가 없습니다. 신규 등록을 사용하세요.</p>
                        ) : candidates.map(c => (
                            <label key={c.id} className={`pb-candidate ${selectedCustomerId === c.id ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="customer"
                                    checked={selectedCustomerId === c.id}
                                    onChange={() => setSelectedCustomerId(c.id)}
                                />
                                <div>
                                    <div><strong>{c.customerName}</strong></div>
                                    <div className="pb-meta">
                                        포인트 {c.points || 0}P
                                        {c.last_visit && ` · 마지막 방문 ${formatDate(c.last_visit)}`}
                                        {c.via === 'plate' && ' · 같은 차량 등록'}
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>
                )}

                {mode === 'new' && (
                    <div className="pb-new-form">
                        <label>
                            고객명 *
                            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="홍길동" />
                        </label>
                        <label>
                            전화번호 (선택)
                            <input
                                value={newPhone}
                                onChange={e => setNewPhone(e.target.value)}
                                placeholder={pb.phone_hint_masked ? `힌트: ${pb.phone_hint_masked}` : '010-1234-5678'}
                            />
                        </label>
                    </div>
                )}

                <div className="pb-actions">
                    <button className="pb-btn-reject" onClick={onReject}>거절</button>
                    <button className="pb-btn-cancel" onClick={onCancel}>취소</button>
                    <button className="pb-btn-approve" onClick={submit}>승인</button>
                </div>
            </div>
        </div>
    );
}

function dedup(arr, key) {
    const seen = new Set();
    return arr.filter(x => {
        if (seen.has(x[key])) return false;
        seen.add(x[key]);
        return true;
    });
}

function formatDateTime(s) {
    if (!s) return '';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return `${d.getMonth() + 1}/${d.getDate()} (${'일월화수목금토'[d.getDay()]}) ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatDate(s) {
    if (!s) return '';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

export default PendingBookings;
